import { FC, MouseEvent } from 'react'
import { ToggleButton, Typography } from '@mui/material'
import { useThemeColors } from 'hooks/useThemeColors'
import { Paper, StyledToggleButtonGroup } from './viewToggle.styles'

type ViewType = 'current' | 'trend'

interface Props {
  activeView: ViewType
  onViewChange: (event: MouseEvent<HTMLElement>, newAlignment: string) => void
  paperSx?: Record<string, unknown>
  ssm?: boolean
}

const ViewToggle: FC<Props> = ({ activeView, onViewChange, paperSx, ssm }) => {
  const getColor = useThemeColors()
  const activeColor = getColor('cta')
  // Dark values are the original toggle colours; light matches the redesign.
  const inactiveColor = getColor('calendar-day')
  const hoverColor = getColor('legacy-neutral-2')
  const containerBg = getColor('toggle-inactive-bg')
  const selectedBg = getColor('toggle-selected-bg')

  return (
    <div style={{ marginLeft: '16px' }}>
      <Paper elevation={0} activeView={activeView} sx={paperSx}>
        <StyledToggleButtonGroup
          size="small"
          value="left"
          exclusive
          onChange={onViewChange}
          aria-label="performance chart view"
        >
          <ToggleButton
            value="current"
            aria-label="performance chart view current"
            sx={{
              background: activeView === 'current' ? selectedBg : containerBg,

              '&:hover': {
                color: `${hoverColor} !important`,

                '.MuiTypography-root': {
                  color: `${hoverColor} !important`
                },

                svg: {
                  path: {
                    stroke: activeView === 'trend' ? `${activeColor} !important` : '',
                    fill: activeView === 'trend' ? `transparent !important` : ''
                  }
                }
              }
            }}
          >
            {activeView === 'current' ? (
              <>
                {ssm ? (
                  <>
                    <SSMToggledIcon color={activeColor} />
                    <Typography className="active">Coverage</Typography>
                  </>
                ) : (
                  <PieChartIcon color={activeColor} />
                )}
              </>
            ) : (
              <>
                {ssm ? (
                  <>
                    <SSMIcon color={inactiveColor} />
                    <Typography>Coverage</Typography>
                  </>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8.3331 2.66661C7.02952 2.96881 5.83053 3.61428 4.8605 4.53606C3.89047 5.45784 3.18472 6.62236 2.81646 7.90884C2.4482 9.19532 2.43083 10.5569 2.76617 11.8524C3.10151 13.1478 3.77734 14.3299 4.72355 15.2762C5.66977 16.2224 6.85191 16.8982 8.14736 17.2335C9.44281 17.5689 10.8044 17.5515 12.0909 17.1833C13.3773 16.815 14.5419 16.1092 15.4637 15.1392C16.3854 14.1692 17.0309 12.9702 17.3331 11.6666C17.3331 11.4456 17.2453 11.2336 17.089 11.0774C16.9327 10.9211 16.7208 10.8333 16.4998 10.8333H13.3331C13.1816 11.4158 12.8789 11.9479 12.4557 12.3759C12.0324 12.8038 11.5037 13.1123 10.9229 13.2702C10.3421 13.4282 9.72987 13.4298 9.14821 13.2751C8.56655 13.1204 8.03608 12.8148 7.61049 12.3892C7.1849 11.9636 6.87928 11.4332 6.72457 10.8515C6.56986 10.2698 6.57155 9.65764 6.72947 9.07685C6.88738 8.49605 7.19592 7.96728 7.62385 7.54404C8.05179 7.1208 8.58393 6.81811 9.16643 6.66661V3.33328C9.15608 3.23455 9.12623 3.13886 9.07861 3.05175C9.031 2.96465 8.96656 2.88786 8.88904 2.82585C8.81153 2.76383 8.72247 2.71783 8.62704 2.69049C8.5316 2.66316 8.43169 2.65504 8.3331 2.66661Z"
                      stroke={inactiveColor}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12.5 2.91663C13.5581 3.2892 14.5192 3.89438 15.3124 4.68759C16.1056 5.48081 16.7108 6.44186 17.0833 7.49996H13.3333C13.0778 7.20075 12.7992 6.92212 12.5 6.66663V2.91663Z"
                      stroke={inactiveColor}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </>
            )}
          </ToggleButton>
          <ToggleButton
            value="trend"
            aria-label="performance chart view trend"
            sx={{
              background: activeView === 'trend' ? selectedBg : containerBg,

              '&:hover': {
                color: `${hoverColor} !important`,

                '.MuiTypography-root': {
                  color: `${hoverColor} !important`
                }
              }
            }}
          >
            {activeView === 'trend' ? (
              <>
                {ssm ? (
                  <>
                    <BudgetToggledIcon color={activeColor} />
                    <Typography className="active">Budget</Typography>
                  </>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1.875 1.875C1.52982 1.875 1.25 2.15482 1.25 2.5C1.25 2.84518 1.52982 3.125 1.875 3.125H2.5V11.875C2.5 13.2557 3.61929 14.375 5 14.375H6.00786L5.03207 17.3024C4.92292 17.6298 5.09989 17.9838 5.42736 18.0929C5.75482 18.2021 6.10877 18.0251 6.21793 17.6976L6.49214 16.875H13.5079L13.7821 17.6976C13.8912 18.0251 14.2452 18.2021 14.5726 18.0929C14.9001 17.9838 15.0771 17.6298 14.9679 17.3024L13.9921 14.375H15C16.3807 14.375 17.5 13.2557 17.5 11.875V3.125H18.125C18.4702 3.125 18.75 2.84518 18.75 2.5C18.75 2.15482 18.4702 1.875 18.125 1.875H1.875ZM7.32547 14.375H12.6745L13.0912 15.625H6.90881L7.32547 14.375ZM14.063 6.87889C14.3617 6.70605 14.4638 6.32372 14.291 6.02494C14.1182 5.72615 13.7358 5.62405 13.437 5.79689C12.2981 6.45573 11.2931 7.31976 10.4723 8.33845L9.19194 7.05813C8.94786 6.81405 8.55214 6.81405 8.30806 7.05813L5.80806 9.55813C5.56398 9.8022 5.56398 10.1979 5.80806 10.442C6.05214 10.6861 6.44786 10.6861 6.69194 10.442L8.75 8.38395L10.0977 9.73167C10.2285 9.86248 10.4106 9.92862 10.5948 9.91229C10.7791 9.89596 10.9467 9.79883 11.0524 9.64705C11.8391 8.51806 12.8678 7.57023 14.063 6.87889Z"
                      fill={activeColor}
                    />
                  </svg>
                )}
              </>
            ) : (
              <>
                {ssm ? (
                  <>
                    <BudgetIcon color={inactiveColor} />
                    <Typography>Budget</Typography>
                  </>
                ) : (
                  <TrendIcon color={inactiveColor} />
                )}
              </>
            )}
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Paper>
    </div>
  )
}

export default ViewToggle

const PieChartIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.41023 2.17508C9.74023 2.44008 9.9519 2.82508 9.99606 3.24675L10.0002 3.33342V6.66675C10.0003 6.85124 9.93911 7.03053 9.8263 7.17652C9.71349 7.3225 9.55543 7.42693 9.3769 7.47342C8.9364 7.58788 8.53396 7.81669 8.21031 8.13668C7.88666 8.45667 7.6533 8.85649 7.53384 9.29566C7.41438 9.73483 7.41306 10.1978 7.53003 10.6376C7.64699 11.0775 7.87808 11.4786 8.1999 11.8004C8.52173 12.1222 8.92286 12.3533 9.3627 12.4703C9.80255 12.5873 10.2655 12.5859 10.7047 12.4665C11.1438 12.347 11.5436 12.1137 11.8636 11.79C12.1836 11.4664 12.4124 11.0639 12.5269 10.6234C12.5734 10.4449 12.6778 10.2868 12.8238 10.174C12.9698 10.0612 13.1491 10 13.3336 10.0001H16.5002C16.9423 10.0001 17.3662 10.1757 17.6787 10.4882C17.9913 10.8008 18.1669 11.2247 18.1669 11.6668C18.1669 11.7301 18.1596 11.7933 18.1452 11.8551C17.8095 13.3035 17.0923 14.6357 16.0681 15.7135C15.0439 16.7913 13.7499 17.5755 12.3205 17.9847C10.8911 18.3939 9.37824 18.4132 7.93885 18.0406C6.49946 17.668 5.18597 16.917 4.13462 15.8657C3.08327 14.8143 2.33236 13.5009 1.95976 12.0615C1.58716 10.6221 1.60645 9.10921 2.01563 7.67979C2.42481 6.25036 3.20897 4.95645 4.28678 3.93225C5.3646 2.90805 6.69681 2.19086 8.14523 1.85508L8.19273 1.84675L8.23606 1.83842C8.44432 1.81399 8.65535 1.83117 8.85692 1.88897C9.05848 1.94676 9.24656 2.04401 9.41023 2.17508ZM12.4961 2.08342L12.5336 2.08508L12.5894 2.08842L12.6569 2.10008L12.6836 2.10342L12.7436 2.12175L12.7769 2.13008C13.9112 2.52943 14.9456 3.16953 15.8091 4.00643C16.6726 4.84333 17.3447 5.8572 17.7794 6.97842L17.8694 7.22342C17.9136 7.34906 17.9271 7.48347 17.9086 7.61539C17.8902 7.74731 17.8404 7.87288 17.7635 7.9816C17.6865 8.09031 17.5846 8.17899 17.4663 8.2402C17.348 8.30142 17.2168 8.33338 17.0836 8.33342H13.3336C13.2132 8.33343 13.0943 8.30736 12.9849 8.25701C12.8756 8.20666 12.7785 8.13322 12.7002 8.04175C12.4729 7.77543 12.2249 7.52745 11.9586 7.30009C11.8671 7.22185 11.7937 7.12472 11.7433 7.01539C11.693 6.90606 11.6669 6.78712 11.6669 6.66675V2.91675C11.6675 2.85064 11.6747 2.78675 11.6886 2.72508L11.7136 2.64008C11.7455 2.54959 11.7929 2.46529 11.8536 2.39092L11.8786 2.36342L11.9111 2.32758C11.9344 2.3039 11.9592 2.28164 11.9852 2.26092L12.0277 2.23258L12.0527 2.21342L12.0902 2.19258L12.1336 2.16758C12.2465 2.11256 12.3704 2.08379 12.4961 2.08342Z"
      fill={color}
    />
  </svg>
)

const TrendIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.125 2.5V11.875C3.125 12.9105 3.96447 13.75 5 13.75H6.875M3.125 2.5H1.875M3.125 2.5H16.875M16.875 2.5H18.125M16.875 2.5V11.875C16.875 12.9105 16.0355 13.75 15 13.75H13.125M6.875 13.75H13.125M6.875 13.75L6.04167 16.25M13.125 13.75L13.9583 16.25M13.9583 16.25L14.375 17.5M13.9583 16.25H6.04167M6.04167 16.25L5.625 17.5M6.25 10L8.75 7.5L10.5397 9.28966C11.3785 8.08584 12.4753 7.07519 13.75 6.33782"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SSMIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.125 4.99984C5.78802 4.99984 8.20792 3.9589 10 2.26172C11.7921 3.9589 14.212 4.99984 16.875 4.99984C16.9173 4.99984 16.9596 4.99958 17.0018 4.99906C17.3251 5.98248 17.5 7.03325 17.5 8.12488C17.5 12.7845 14.3131 16.6997 10 17.8098C5.68693 16.6997 2.5 12.7845 2.5 8.12488C2.5 7.03325 2.67491 5.98248 2.99825 4.99906C3.04043 4.99958 3.08269 4.99984 3.125 4.99984Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SSMToggledIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.125 4.99984C5.78802 4.99984 8.20792 3.9589 10 2.26172C11.7921 3.9589 14.212 4.99984 16.875 4.99984C16.9173 4.99984 16.9596 4.99958 17.0018 4.99906C17.3251 5.98248 17.5 7.03325 17.5 8.12488C17.5 12.7845 14.3131 16.6997 10 17.8098C5.68693 16.6997 2.5 12.7845 2.5 8.12488C2.5 7.03325 2.67491 5.98248 2.99825 4.99906C3.04043 4.99958 3.08269 4.99984 3.125 4.99984Z"
      fill={color}
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const BudgetToggledIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 6.25C8.96447 6.25 8.125 7.08947 8.125 8.125C8.125 9.16053 8.96447 10 10 10C11.0355 10 11.875 9.16053 11.875 8.125C11.875 7.08947 11.0355 6.25 10 6.25Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.25 4.0625C1.25 3.19956 1.94956 2.5 2.8125 2.5H17.1875C18.0504 2.5 18.75 3.19956 18.75 4.0625V12.1875C18.75 13.0504 18.0504 13.75 17.1875 13.75H2.8125C1.94955 13.75 1.25 13.0504 1.25 12.1875V4.0625ZM6.875 8.125C6.875 6.39911 8.27411 5 10 5C11.7259 5 13.125 6.39911 13.125 8.125C13.125 9.85089 11.7259 11.25 10 11.25C8.27411 11.25 6.875 9.85089 6.875 8.125ZM15.625 7.5C15.2798 7.5 15 7.77982 15 8.125V8.13125C15 8.47643 15.2798 8.75625 15.625 8.75625H15.6313C15.9764 8.75625 16.2563 8.47643 16.2563 8.13125V8.125C16.2563 7.77982 15.9764 7.5 15.6313 7.5H15.625ZM3.75 8.125C3.75 7.77982 4.02982 7.5 4.375 7.5H4.38125C4.72643 7.5 5.00625 7.77982 5.00625 8.125V8.13125C5.00625 8.47643 4.72643 8.75625 4.38125 8.75625H4.375C4.02982 8.75625 3.75 8.47643 3.75 8.13125V8.125Z"
      fill={color}
    />
    <path
      d="M1.875 15C1.52982 15 1.25 15.2798 1.25 15.625C1.25 15.9702 1.52982 16.25 1.875 16.25C6.37504 16.25 10.7335 16.8518 14.8748 17.9791C15.867 18.2491 16.875 17.514 16.875 16.4626V15.625C16.875 15.2798 16.5952 15 16.25 15H1.875Z"
      fill={color}
    />
  </svg>
)

const BudgetIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.875 15.625C6.431 15.625 10.8447 16.2344 15.039 17.376C15.6448 17.5409 16.25 17.0905 16.25 16.4626V15.625M3.125 3.75V4.375C3.125 4.72018 2.84518 5 2.5 5H1.875M1.875 5V4.6875C1.875 4.16973 2.29473 3.75 2.8125 3.75H16.875M1.875 5V12.5M16.875 3.75V4.375C16.875 4.72018 17.1548 5 17.5 5H18.125M16.875 3.75H17.1875C17.7053 3.75 18.125 4.16973 18.125 4.6875V12.8125C18.125 13.3303 17.7053 13.75 17.1875 13.75H16.875M18.125 12.5H17.5C17.1548 12.5 16.875 12.7798 16.875 13.125V13.75M16.875 13.75H3.125M3.125 13.75H2.8125C2.29473 13.75 1.875 13.3303 1.875 12.8125V12.5M3.125 13.75V13.125C3.125 12.7798 2.84518 12.5 2.5 12.5H1.875M12.5 8.75C12.5 10.1307 11.3807 11.25 10 11.25C8.61929 11.25 7.5 10.1307 7.5 8.75C7.5 7.36929 8.61929 6.25 10 6.25C11.3807 6.25 12.5 7.36929 12.5 8.75ZM15 8.75H15.0062V8.75625H15V8.75ZM5 8.75H5.00625V8.75625H5V8.75Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
